import { CheckCircle2Icon, LockKeyholeIcon, ShieldCheckIcon, UsersIcon } from "lucide-react";

import type { WorkspaceAdminResponse } from "../../../shared/schemas";
import {
  AddWorkspaceUserDialog,
  ManageWorkspaceUserAccessDialog,
} from "../../components/WorkspaceUserDialogs";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";

const roleLabels = {
  platform_admin: "Consultant",
  organization_admin: "Workspace administrator",
  member: "Member",
} as const;

function userInitials(displayName: string) {
  return displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function WorkspaceStats({ workspace }: { workspace: WorkspaceAdminResponse }) {
  const administratorCount = workspace.members.filter((member) => member.role !== "member").length;
  const stats = [
    { icon: UsersIcon, label: "Workspace users", value: workspace.members.length },
    { icon: ShieldCheckIcon, label: "Administrators", value: administratorCount },
    { icon: CheckCircle2Icon, label: "Available tools", value: workspace.tools.length },
  ];
  return (
    <div className="mb-5 grid gap-3 sm:grid-cols-3">
      {stats.map(({ icon: Icon, label, value }) => (
        <div
          className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3 shadow-card"
          key={label}
        >
          <Icon aria-hidden="true" className="size-5 text-primary" />
          <div>
            <strong className="block font-heading text-xl leading-none tabular-nums">
              {value}
            </strong>
            <span className="text-xs text-muted-foreground">{label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function WorkspaceMemberRow({
  isOnline,
  member,
  organizationId,
  workspace,
  onUpdated,
}: {
  isOnline: boolean;
  member: WorkspaceAdminResponse["members"][number];
  organizationId: string;
  workspace: WorkspaceAdminResponse;
  onUpdated: () => Promise<void>;
}) {
  const administrator = member.role !== "member";
  const accessCount = member.toolAccess.length;
  const accessPercent =
    workspace.tools.length === 0 ? 0 : Math.round((accessCount / workspace.tools.length) * 100);
  return (
    <div className="grid gap-4 border-b p-4 last:border-b-0 sm:grid-cols-[minmax(0,1.5fr)_minmax(10rem,0.8fr)_auto] sm:items-center sm:p-5">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar size="lg">
          <AvatarFallback>{userInitials(member.user.displayName)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <h3 className="m-0 truncate text-base font-bold">{member.user.displayName}</h3>
          <p className="mt-0.5 mb-0 truncate font-mono text-xs text-muted-foreground">
            @{member.user.username}
          </p>
        </div>
      </div>
      <div className="grid gap-2">
        <Badge variant="outline">{roleLabels[member.role]}</Badge>
        <div className="flex items-center gap-2 text-sm">
          {administrator ? (
            <>
              <LockKeyholeIcon aria-hidden="true" className="size-4 text-muted-foreground" />
              <span>All tools · role-based</span>
            </>
          ) : (
            <span>
              <strong className="tabular-nums">{accessCount}</strong> of {workspace.tools.length}{" "}
              tools
            </span>
          )}
        </div>
        {!administrator ? (
          <div
            aria-label={`${accessPercent}% of tools granted`}
            className="h-1.5 overflow-hidden rounded-full bg-muted"
            role="img"
          >
            <div className="h-full bg-primary" style={{ width: `${accessPercent}%` }} />
          </div>
        ) : null}
      </div>
      <div className="flex justify-end">
        {administrator ? (
          <span className="text-xs text-muted-foreground">Access cannot be removed</span>
        ) : (
          <ManageWorkspaceUserAccessDialog
            isOnline={isOnline}
            member={member}
            organizationId={organizationId}
            tools={workspace.tools}
            onUpdated={onUpdated}
          />
        )}
      </div>
    </div>
  );
}

export function WorkspaceUsersPanel({
  isOnline,
  mutationMessage,
  organizationId,
  successMessage,
  workspace,
  onCreated,
  onUpdated,
}: {
  isOnline: boolean;
  mutationMessage: string | null;
  organizationId: string;
  successMessage: string;
  workspace: WorkspaceAdminResponse;
  onCreated: (
    result: Parameters<
      NonNullable<React.ComponentProps<typeof AddWorkspaceUserDialog>["onCreated"]>
    >[0],
  ) => Promise<void>;
  onUpdated: () => Promise<void>;
}) {
  return (
    <section aria-labelledby="workspace-users-heading" className="py-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2
            className="m-0 text-balance font-heading text-2xl font-bold"
            id="workspace-users-heading"
          >
            Workspace users
          </h2>
          <p className="mt-2 mb-0 text-pretty text-muted-foreground">
            Add people, review their roles, and manage the tools they can open.
          </p>
        </div>
        <AddWorkspaceUserDialog
          isOnline={isOnline}
          organizationId={organizationId}
          workspace={workspace}
          onCreated={onCreated}
        />
      </div>
      <WorkspaceStats workspace={workspace} />
      {successMessage ? (
        <Alert className="mb-5" role="status">
          <CheckCircle2Icon aria-hidden="true" />
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      ) : null}
      {mutationMessage ? (
        <Alert className="mb-5" role="alert" variant="destructive">
          <AlertDescription>{mutationMessage}</AlertDescription>
        </Alert>
      ) : null}
      <div className="overflow-hidden rounded-lg border bg-card shadow-card">
        {workspace.members.map((member) => (
          <WorkspaceMemberRow
            isOnline={isOnline}
            key={member.membershipId}
            member={member}
            organizationId={organizationId}
            workspace={workspace}
            onUpdated={onUpdated}
          />
        ))}
      </div>
    </section>
  );
}
